export type ChangeType = 'feature' | 'fix' | 'optimization'

export interface ChangelogEntry {
  version: string
  date: string
  title: string
  changes: { type: ChangeType; text: string }[]
}

/**
 * Newest first. The app's displayed version derives from the top entry.
 * The post-commit hook (.githooks/post-commit) adds an entry and bumps
 * package.json for every commit; see README → Versioning.
 */
export const CHANGELOG: ChangelogEntry[] = [
  {
    version: '0.5.38',
    date: 'Oct 1, 2026',
    title: 'Give the new priority name field a standard input style',
    changes: [
      { type: 'feature', text: 'Give the new priority name field a standard input style' },
    ],
  },
  {
    version: '0.5.37',
    date: 'Oct 1, 2026',
    title: 'Make Add priority and Add area note real buttons',
    changes: [
      { type: 'feature', text: 'Outline buttons, 44px tall on phones (36px on desktop) instead of small text links' },
    ],
  },
  {
    version: '0.5.36',
    date: 'Oct 1, 2026',
    title: 'Drag bottom sheets down from anywhere to dismiss',
    changes: [
      { type: 'feature', text: 'The sheet follows the finger and the scrim fades with it; a flick or a drag past ~40%' },
      { type: 'feature', text: 'Scrolled content scrolls first; horizontal swipes and text fields are left alone' },
      { type: 'feature', text: 'Starting a drag dismisses the keyboard, and a drag never also counts as a tap' },
    ],
  },
  {
    version: '0.5.35',
    date: 'Oct 1, 2026',
    title: 'Simplify the life area menu to Edit and Remove',
    changes: [
      { type: 'optimization', text: 'Drop the desktop-only Target allocation and Change color items; both are in the Edit form' },
    ],
  },
  {
    version: '0.5.34',
    date: 'Oct 1, 2026',
    title: 'Lock phones at 1x: no zoom on field focus, pinch or double-tap',
    changes: [
      { type: 'feature', text: 'Viewport maximum-scale=1 and user-scalable=no stop iOS zooming into focused fields' },
      { type: 'feature', text: 'touch-action and cancelled Safari gesture events block pinch and double-tap zoom on' },
    ],
  },
  {
    version: '0.5.32',
    date: 'Oct 1, 2026',
    title: 'feat(home): blank-slate empty state with a hand-drawn arrow',
    changes: [
      { type: 'feature', text: 'feat(home): blank-slate empty state with a hand-drawn arrow' },
    ],
  },
  {
    version: '0.5.30',
    date: 'Oct 1, 2026',
    title: 'feat(users): start new accounts as a blank slate',
    changes: [
      { type: 'feature', text: 'feat(users): start new accounts as a blank slate' },
    ],
  },
  {
    version: '0.5.29',
    date: 'Sep 27, 2026',
    title: 'will prevent these commits in future',
    changes: [
      { type: 'feature', text: 'will prevent these commits in future' },
    ],
  },
  {
    version: '0.5.28',
    date: 'Sep 27, 2026',
    title: 'Priority tiles: menu is the only control, full-height drag edge, marked coach notes',
    changes: [
      { type: 'feature', text: 'Priority tiles are read-only everywhere: status, cadence, notes and title change in the edit view' },
      { type: 'feature', text: 'Status shows as a label beside the menu on desktop, under the title on phones' },
      { type: 'feature', text: 'Drag a tile from anywhere along its left edge, top to bottom' },
      { type: 'feature', text: 'The coach\'s note on a priority is in the main text color, marked with the AI sparkle' },
    ],
  },
  {
    version: '0.5.27',
    date: 'Sep 27, 2026',
    title: 'Fix coach notes keyed to life areas instead of priorities',
    changes: [
      { type: 'fix', text: 'Fix coach notes landing on labels like #3000: life areas are listed by position, not sort value' },
      { type: 'feature', text: 'Tell the model to copy priority ids exactly and never key notes by area, rank or title' },
      { type: 'optimization', text: 'Drop any coach note whose id isn\'t one of the priorities the coach was shown' },
    ],
  },
  {
    version: '0.5.26',
    date: 'Sep 27, 2026',
    title: 'Give priority tiles an even border and 16px spacing on desktop too',
    changes: [
      { type: 'feature', text: 'Give priority tiles an even border and 16px spacing on desktop too' },
    ],
  },
  {
    version: '0.5.25',
    date: 'Sep 27, 2026',
    title: 'Simplify priority tiles on phones',
    changes: [
      { type: 'feature', text: 'Titles on phones use the tile\'s full width and wrap instead of truncating' },
      { type: 'feature', text: 'Status sits under the title beside Under-target, as a label (change it in the edit view)' },
      { type: 'optimization', text: 'Remove the cadence and notes buttons from tiles on phones; both live in the edit view' },
      { type: 'optimization', text: 'Simplify the priority menu on phones to Edit details and Archive' },
      { type: 'feature', text: 'Tiles on phones get an even 1px border and 16px between them' },
    ],
  },
  {
    version: '0.5.24',
    date: 'Sep 27, 2026',
    title: 'committing the rest',
    changes: [
      { type: 'feature', text: 'committing the rest' },
    ],
  },
  {
    version: '0.5.23',
    date: 'Sep 27, 2026',
    title: 'Letter footer: time in CT, thinking words, estimate vs actual, month totals',
    changes: [
      { type: 'feature', text: 'Each letter shows when it was written, in US Central time' },
      { type: 'feature', text: 'Shows how many words the coach thought before writing' },
      { type: 'feature', text: 'Compares a letter\'s actual cost with the estimate shown before Generate' },
      { type: 'feature', text: 'Running totals for the month, and roughly how much prepaid API credit is left' },
    ],
  },
  {
    version: '0.5.22',
    date: 'Sep 27, 2026',
    title: 'Instant coaching toggles with haptics, and cost and tokens under each letter',
    changes: [
      { type: 'feature', text: 'Coaching style and model toggles respond instantly; saving and re-pricing run in the background' },
      { type: 'feature', text: 'Haptic taps on iPhone: 1 to 4 for Haiku through Fable, 1 for each coaching style' },
      { type: 'feature', text: 'Settings changes show at once and roll back if the save fails' },
      { type: 'feature', text: 'Each letter now ends with what it cost and its tokens in and out' },
    ],
  },
  {
    version: '0.5.21',
    date: 'Sep 27, 2026',
    title: 'Coaching style and model become glass segmented controls on phones',
    changes: [
      { type: 'feature', text: 'Full-width, iOS-style segmented controls for coaching style and model on phones' },
      { type: 'feature', text: 'The selected segment\'s glass thumb springs between options and stretches as it lands' },
      { type: 'feature', text: 'Honors Reduce Motion; larger screens keep the compact pills' },
    ],
  },
  {
    version: '0.5.20',
    date: 'Sep 27, 2026',
    title: 'Put coaching style and model side by side, with quieter cost chips',
    changes: [
      { type: 'feature', text: 'Put coaching style and model side by side, with quieter cost chips' },
    ],
  },
  {
    version: '0.5.19',
    date: 'Sep 27, 2026',
    title: 'Group letter settings with Generate, and widen the coaching panel',
    changes: [
      { type: 'feature', text: 'Group letter settings with Generate, and widen the coaching panel' },
    ],
  },
  {
    version: '0.5.18',
    date: 'Sep 27, 2026',
    title: 'Choose the model next to the coaching style',
    changes: [
      { type: 'feature', text: 'Choose the model next to the coaching style' },
    ],
  },
  {
    version: '0.5.17',
    date: 'Sep 27, 2026',
    title: 'Let each letter use a chosen model, priced per model',
    changes: [
      { type: 'feature', text: 'Let each letter use a chosen model, priced per model' },
    ],
  },
  {
    version: '0.5.16',
    date: 'Sep 27, 2026',
    title: 'keeping example dev vars file',
    changes: [
      { type: 'feature', text: 'keeping example dev vars file' },
    ],
  },
  {
    version: '0.5.15',
    date: 'Sep 27, 2026',
    title: 'Confirm before deleting a coaching letter',
    changes: [
      { type: 'feature', text: 'Confirm before deleting a coaching letter' },
    ],
  },
  {
    version: '0.5.14',
    date: 'Sep 27, 2026',
    title: 'Show the letter\'s thinking and text as they stream in',
    changes: [
      { type: 'feature', text: 'Show the letter\'s thinking and text as they stream in' },
    ],
  },
  {
    version: '0.5.13',
    date: 'Sep 27, 2026',
    title: 'Stream coaching letters with summarized thinking from the server',
    changes: [
      { type: 'feature', text: 'Stream coaching letters with summarized thinking from the server' },
    ],
  },
  {
    version: '0.5.12',
    date: 'Sep 26, 2026',
    title: 'ignoring sensitive data files',
    changes: [
      { type: 'feature', text: 'ignoring sensitive data files' },
    ],
  },
  {
    version: '0.5.11',
    date: 'Sep 26, 2026',
    title: 'Move the iOS app to the app.myplumb bundle ID',
    changes: [
      { type: 'feature', text: 'Move the iOS app to the app.myplumb bundle ID' },
    ],
  },
  {
    version: '0.5.10',
    date: 'Sep 26, 2026',
    title: 'Tie all data to the signed-in Google account',
    changes: [
      { type: 'feature', text: 'Tie all data to the signed-in Google account' },
    ],
  },
  {
    version: '0.5.9',
    date: 'Sep 26, 2026',
    title: 'Run server tests against an in-memory D1 database',
    changes: [
      { type: 'feature', text: 'Run server tests against an in-memory D1 database' },
    ],
  },
  {
    version: '0.5.8',
    date: 'Sep 26, 2026',
    title: 'Show an estimated cost before generating a coaching letter',
    changes: [
      { type: 'feature', text: 'Show an estimated cost before generating a coaching letter' },
    ],
  },
  {
    version: '0.5.7',
    date: 'Sep 26, 2026',
    title: 'Remove the color dot from the priority detail back link',
    changes: [
      { type: 'optimization', text: 'Remove the color dot from the priority detail back link' },
    ],
  },
  {
    version: '0.5.6',
    date: 'Sep 26, 2026',
    title: 'Remove Move up and Move down from the priority menu',
    changes: [
      { type: 'optimization', text: 'Remove Move up and Move down from the priority menu' },
    ],
  },
  {
    version: '0.5.5',
    date: 'Sep 26, 2026',
    title: 'Reduce header and priority tile icons to 24px on phones',
    changes: [
      { type: 'optimization', text: 'Reduce header and priority tile icons to 24px on phones' },
    ],
  },
  {
    version: '0.5.4',
    date: 'Sep 26, 2026',
    title: 'Mobile polish: half-height area drawer, bigger icons and wordmark',
    changes: [
      { type: 'feature', text: 'Life Areas drawer opens to half the screen on phones' },
      { type: 'feature', text: 'Larger Plumb wordmark on phones (1.65rem)' },
      { type: 'feature', text: 'Coaching letter and Weekly review sit side by side on phones' },
      { type: 'optimization', text: 'Simplify Settings: export and import now share one Your data section' },
      { type: 'optimization', text: 'Simplify the area menu on phones to Edit and Delete (target and color are in Edit)' },
      { type: 'feature', text: 'Priority tiles center their status and tools vertically' },
      { type: 'feature', text: 'Header and priority tile icons are 32px on phones' },
    ],
  },
  {
    version: '0.5.3',
    date: 'Sep 26, 2026',
    title: 'Add a ?notch preview of iPhone safe areas for local dev',
    changes: [
      { type: 'feature', text: 'Add a ?notch preview of iPhone safe areas for local dev' },
    ],
  },
  {
    version: '0.5.2',
    date: 'Sep 26, 2026',
    title: 'Separate dev builds from TestFlight on iPhone',
    changes: [
      { type: 'feature', text: 'Local test builds install as Plumb Dev, with a white DEV icon, next to the TestFlight app' },
      { type: 'fix', text: 'Fix sign-in returning to the wrong app when both builds are installed' },
    ],
  },
  {
    version: '0.5.1',
    date: 'Sep 26, 2026',
    title: 'Sign Debug and Release with the paid developer team',
    changes: [
      { type: 'feature', text: 'Sign Debug and Release with the paid developer team' },
    ],
  },
  {
    version: '0.5.0',
    date: 'Sep 26, 2026',
    title: 'Cloud, sign-in, and iPhone',
    changes: [
      { type: 'feature', text: 'Plumb now lives at myplumb.app, with your data in a hosted Cloudflare database.' },
      { type: 'feature', text: 'Sign in with Google. Only allowlisted accounts can get in.' },
      { type: 'feature', text: 'iPhone app (TestFlight), which keeps you signed in through the Keychain.' },
      { type: 'feature', text: 'Header shows who is signed in, with sign out one tap away.' },
      { type: 'feature', text: 'New type: Gabriela for headings and navigation, Lato for everything else.' },
      { type: 'fix', text: 'Layout stays clear of the notch, status bar, and home indicator on iPhone.' },
      { type: 'fix', text: 'Dates follow your local day instead of rolling over early in the evening.' },
      { type: 'optimization', text: 'Lists refresh in the background once a minute instead of every 12 seconds.' },
      { type: 'optimization', text: 'Each change now bumps the version and is listed here automatically.' },
    ],
  },
  {
    version: '0.4.1',
    date: 'Jul 2026',
    title: 'Detail view refinements',
    changes: [
      { type: 'feature', text: 'App icon: Plumb now has a favicon and home-screen icons for phone, tablet, and desktop.' },
      { type: 'optimization', text: 'Slimmer life-area header — a flat 54px bar tinted with the area color replaces the tall gradient hero.' },
      { type: 'optimization', text: 'Target % and color selection moved into the area edit menu.' },
      { type: 'optimization', text: 'Life areas now order themselves by number of active priorities (alphabetical on ties) — manual reordering removed.' },
      { type: 'optimization', text: 'Area notes open as a recessed drawer, clearly separated from the priorities list.' },
      { type: 'optimization', text: 'Removed on-screen keyboard-shortcut hints and the redundant "Priorities" heading.' },
    ],
  },
  {
    version: '0.4.0',
    date: 'Jul 2026',
    title: 'Mobile experience',
    changes: [
      { type: 'feature', text: 'Bottom-sheet UI on phones: life-area picker, settings, cadence, notes, and calendar focus all open as draggable sheets.' },
      { type: 'feature', text: 'Cadence options are now explicit buttons, with a 15-minute-step slider for hours per week on mobile.' },
      { type: 'feature', text: 'Calendar focus filter groups priorities under their life area.' },
      { type: 'feature', text: 'Version footer with these release notes.' },
      { type: 'fix', text: 'Controls that only appeared on mouse hover are now always visible on touch screens.' },
      { type: 'fix', text: '"Delete everything" moved behind an extra confirmation pane with a full explanation.' },
      { type: 'optimization', text: 'One journal search field with an "includes notes" escalation instead of two separate searches.' },
      { type: 'optimization', text: 'Larger tap targets, icon navigation on small screens, and theme control moved into Settings.' },
    ],
  },
  {
    version: '0.3.0',
    date: 'Jul 2026',
    title: 'Server persistence & self-hosting',
    changes: [
      { type: 'feature', text: 'All data now lives in a server-side SQLite database instead of the browser, so every device sees the same state.' },
      { type: 'feature', text: 'Docker deployment for home servers (Synology NAS guide included).' },
      { type: 'feature', text: 'AI coaching can run fully local through Ollama — no cloud key required.' },
    ],
  },
  {
    version: '0.2.0',
    date: 'Jun 2026',
    title: 'Coaching & review',
    changes: [
      { type: 'feature', text: 'Weekly review wizard with AI coaching woven into the insights step.' },
      { type: 'feature', text: 'Coaching letters with history, tone calibration, and per-item AI exclusion for privacy.' },
      { type: 'feature', text: 'Two-panel detail view with search and filter polish.' },
    ],
  },
  {
    version: '0.1.0',
    date: 'May 2026',
    title: 'Foundation',
    changes: [
      { type: 'feature', text: 'Life areas and priorities with drag ranking, status lifecycle, notes, and change history.' },
      { type: 'feature', text: 'Journal with free writing, discernment, and weekly review entry types.' },
      { type: 'feature', text: 'Calendar with year, month, and week allocation views.' },
      { type: 'feature', text: 'Command palette, keyboard shortcuts, light and dark themes.' },
    ],
  },
]

export const APP_VERSION = CHANGELOG[0]!.version
